import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMiniERP } from './hooks/useMiniERP';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faFileAlt, faWarehouse,
  faMoneyBill, faBalanceScale, faFilePdf, faUserClock
} from '@fortawesome/free-solid-svg-icons';
import { BarChart, LineChart, DonutChart } from './components/MiniERPChart';
import { generateRegisterPDF, ORIENTATION } from './components/MiniERPPdf';

// A ledger's running balance = opening balance, adjusted by every voucher
// entry posted against it. Debit-normal ledgers grow with debit; credit-normal
// ledgers grow with credit.
const computeLedgerBalance = (ledger, vouchers) => {
  let net = 0;
  vouchers.forEach(v => {
    if (!v.isActive) return;
    v.entries.forEach(en => {
      const entryLedgerId = en.ledgerId?._id || en.ledgerId;
      if (entryLedgerId === ledger._id) {
        net += (en.debit || 0) - (en.credit || 0);
      }
    });
  });
  const opening = ledger.balanceType === 'Debit' ? (ledger.openingBalance || 0) : -(ledger.openingBalance || 0);
  const signedTotal = opening + net;
  return ledger.balanceType === 'Debit' ? signedTotal : -signedTotal;
};

const monthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key) => {
  const [y, m] = key.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleString('default', { month: 'short' });
};

const MiniERPReports = ({ authState }) => {
  const navigate = useNavigate();
  const {
    fetchLedgers,
    fetchVouchers,
    fetchStockTransactions,
    fetchPettyTransactions,
  } = useMiniERP();

  const [ledgers, setLedgers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [pettyTransactions, setPettyTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('accounting');
  const [selectedReport, setSelectedReport] = useState('ledger');
  const [selectedLedgerId, setSelectedLedgerId] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [lRes, vRes, sRes, pRes] = await Promise.all([
        fetchLedgers(),
        fetchVouchers(),
        fetchStockTransactions(),
        fetchPettyTransactions(),
      ]);
      setLedgers(lRes?.data || []);
      setVouchers(vRes?.data || []);
      setStockTransactions(sRes?.data || []);
      setPettyTransactions(pRes?.data || []);
      setLoading(false);
    };
    load();
  }, []);

  const ledgersWithBalance = useMemo(
    () => ledgers.map(l => ({ ...l, balance: computeLedgerBalance(l, vouchers) })),
    [ledgers, vouchers]
  );

  const cashLedgers = ledgersWithBalance.filter(l => /cash/i.test(l.name) && !/petty/i.test(l.name));
  const bankLedgers = ledgersWithBalance.filter(l => /bank/i.test(l.name));
  const pettyCashLedgers = ledgersWithBalance.filter(l => /petty\s*cash/i.test(l.name) || /petty/i.test(l.ledgerCode));

  const entriesForLedgerIds = (ledgerIds) => {
    const idSet = new Set(ledgerIds);
    const rows = [];
    vouchers.forEach(v => {
      if (!v.isActive) return;
      v.entries.forEach(en => {
        const entryLedgerId = en.ledgerId?._id || en.ledgerId;
        if (idSet.has(entryLedgerId)) {
          rows.push({
            date: v.date,
            voucherNumber: v.voucherNumber,
            voucherType: v.voucherType,
            narration: v.narration,
            debit: en.debit || 0,
            credit: en.credit || 0,
          });
        }
      });
    });
    return rows.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const accountingReports = [
    { key: 'ledger', label: 'Ledger View', icon: faFileAlt },
    { key: 'cashbook', label: 'Cash Book', icon: faMoneyBill },
    { key: 'bankbook', label: 'Bank Book', icon: faMoneyBill },
    { key: 'journal', label: 'Journal Register', icon: faFileAlt },
    { key: 'pettycash', label: 'Petty Cash Book', icon: faMoneyBill },
    { key: 'pl', label: 'Profit & Loss', icon: faBalanceScale },
    { key: 'bs', label: 'Balance Sheet', icon: faBalanceScale },
  ];

  const inventoryReports = [
    { key: 'stocklist', label: 'Stock List', icon: faWarehouse },
    { key: 'stockregister', label: 'Stock In/Out Register', icon: faWarehouse },
    { key: 'salesregister', label: 'Sales Register', icon: faMoneyBill },
    { key: 'purchaseregister', label: 'Purchase Register', icon: faMoneyBill },
  ];

  const PdfButton = ({ title, columns, rows, orientation = ORIENTATION.REGISTER }) => (
    <button
      onClick={() => generateRegisterPDF({ title, columns, rows, orientation })}
      className="w-fit px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center gap-2 text-sm"
      title={`Download ${title} PDF`}
    >
      <FontAwesomeIcon icon={faFilePdf} /> PDF
    </button>
  );

  const RegisterTable = ({ rows }) => (
    <>
      {/* ================= MOBILE CARDS ================= */}
      <div className="block lg:hidden space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="bg-gray-900 border border-gray-700 rounded-xl p-3 text-xs">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-mono text-cyan-300">{r.voucherNumber}</div>
                <div className="text-gray-500">{new Date(r.date).toLocaleDateString()} • {r.voucherType}</div>
              </div>
              <div className="text-right">
                {r.debit ? <div className="text-red-400 font-semibold">Dr ₹{r.debit}</div> : null}
                {r.credit ? <div className="text-green-400 font-semibold">Cr ₹{r.credit}</div> : null}
              </div>
            </div>
            {r.narration && <div className="text-gray-400 mt-1">{r.narration}</div>}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-xs">No entries yet.</div>
        )}
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-xs lg:text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3">Date</th>
              <th className="text-left py-3">Voucher No</th>
              <th className="text-left py-3">Type</th>
              <th className="text-left py-3">Narration</th>
              <th className="text-right py-3">Debit</th>
              <th className="text-right py-3">Credit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-700 hover:bg-gray-700/50">
                <td className="py-3">{new Date(r.date).toLocaleDateString()}</td>
                <td className="py-3 font-mono text-cyan-300">{r.voucherNumber}</td>
                <td className="py-3 text-gray-400">{r.voucherType}</td>
                <td className="py-3 text-gray-400 max-w-xs truncate">{r.narration || '-'}</td>
                <td className="py-3 text-right text-red-400">{r.debit ? `₹${r.debit}` : ''}</td>
                <td className="py-3 text-right text-green-400">{r.credit ? `₹${r.credit}` : ''}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">No entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const registerPdfColumns = [
    { header: 'Date', dataKey: 'dateStr' },
    { header: 'Voucher No', dataKey: 'voucherNumber' },
    { header: 'Type', dataKey: 'voucherType' },
    { header: 'Narration', dataKey: 'narration' },
    { header: 'Debit', dataKey: 'debitStr' },
    { header: 'Credit', dataKey: 'creditStr' },
  ];
  const toPdfRows = (rows) => rows.map(r => ({
    ...r,
    dateStr: new Date(r.date).toLocaleDateString(),
    debitStr: r.debit ? `Rs. ${r.debit}` : '',
    creditStr: r.credit ? `Rs. ${r.credit}` : '',
  }));

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'ledger': {
        const rows = selectedLedgerId
          ? entriesForLedgerIds([selectedLedgerId])
          : null;

        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Ledger View</h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedLedgerId}
                  onChange={(e) => setSelectedLedgerId(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                >
                  <option value="">All Ledgers (summary)</option>
                  {ledgersWithBalance.map(l => (
                    <option key={l._id} value={l._id}>{l.ledgerCode} - {l.name}</option>
                  ))}
                </select>
                {!selectedLedgerId ? (
                  <PdfButton
                    title="Ledger Summary"
                    orientation={ORIENTATION.LEDGER}
                    columns={[
                      { header: 'Code', dataKey: 'ledgerCode' },
                      { header: 'Name', dataKey: 'name' },
                      { header: 'Group', dataKey: 'group' },
                      { header: 'Opening', dataKey: 'openingStr' },
                      { header: 'Balance', dataKey: 'balanceStr' },
                    ]}
                    rows={ledgersWithBalance.map(l => ({
                      ...l,
                      openingStr: `Rs. ${l.openingBalance || 0}`,
                      balanceStr: `Rs. ${l.balance.toFixed(2)} ${l.balance >= 0 ? l.balanceType : (l.balanceType === 'Debit' ? 'Credit' : 'Debit')}`,
                    }))}
                  />
                ) : (
                  <PdfButton title={`Ledger - ${selectedLedgerId}`} orientation={ORIENTATION.LEDGER} columns={registerPdfColumns} rows={toPdfRows(rows)} />
                )}
              </div>
            </div>

            {!selectedLedgerId ? (
              <>
                {/* ================= MOBILE CARDS ================= */}
                <div className="block lg:hidden space-y-2">
                  {ledgersWithBalance.map(l => (
                    <div key={l._id} className="bg-gray-900 border border-gray-700 rounded-xl p-3 text-xs">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-mono text-cyan-300">{l.ledgerCode}</div>
                          <div className="text-sm font-semibold text-white">{l.name}</div>
                          <div className="text-gray-500">{l.group}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-500">Opening ₹{l.openingBalance || 0}</div>
                          <div className={`font-medium ${l.balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            ₹{l.balance.toFixed(2)} {l.balance >= 0 ? l.balanceType : (l.balanceType === 'Debit' ? 'Credit' : 'Debit')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ================= DESKTOP TABLE ================= */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-xs lg:text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3">Code</th>
                        <th className="text-left py-3">Ledger Name</th>
                        <th className="text-left py-3">Group</th>
                        <th className="text-right py-3">Opening</th>
                        <th className="text-right py-3">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgersWithBalance.map(l => (
                        <tr key={l._id} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-3 font-mono">{l.ledgerCode}</td>
                          <td className="py-3">{l.name}</td>
                          <td className="py-3 text-gray-400">{l.group}</td>
                          <td className="py-3 text-right">₹{l.openingBalance || 0}</td>
                          <td className={`py-3 text-right font-medium ${l.balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            ₹{l.balance.toFixed(2)} {l.balance >= 0 ? l.balanceType : (l.balanceType === 'Debit' ? 'Credit' : 'Debit')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <RegisterTable rows={rows} />
            )}
          </div>
        );
      }

      case 'cashbook':
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Cash Book</h3>
              {cashLedgers.length > 0 && (
                <PdfButton title="Cash Book" orientation={ORIENTATION.LEDGER} columns={registerPdfColumns} rows={toPdfRows(entriesForLedgerIds(cashLedgers.map(l => l._id)))} />
              )}
            </div>
            {cashLedgers.length === 0 ? (
              <p className="text-gray-500">No "Cash" ledger found. Create one in Ledger Master (e.g. "Cash in Hand").</p>
            ) : (
              <RegisterTable rows={entriesForLedgerIds(cashLedgers.map(l => l._id))} />
            )}
          </div>
        );

      case 'bankbook':
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Bank Book</h3>
              {bankLedgers.length > 0 && (
                <PdfButton title="Bank Book" orientation={ORIENTATION.LEDGER} columns={registerPdfColumns} rows={toPdfRows(entriesForLedgerIds(bankLedgers.map(l => l._id)))} />
              )}
            </div>
            {bankLedgers.length === 0 ? (
              <p className="text-gray-500">No "Bank" ledger found. Create one in Ledger Master (e.g. "Cash at Bank").</p>
            ) : (
              <RegisterTable rows={entriesForLedgerIds(bankLedgers.map(l => l._id))} />
            )}
          </div>
        );

      case 'journal': {
        const journalVouchers = vouchers.filter(v => v.voucherType === 'Journal');
        const journalPdfRows = journalVouchers.map(v => ({
          dateStr: new Date(v.date).toLocaleDateString(),
          voucherNumber: v.voucherNumber,
          narration: v.narration || '-',
          amountStr: `Rs. ${v.totalDebit}`,
        }));
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Journal Register</h3>
              <PdfButton
                title="Journal Register"
                orientation={ORIENTATION.LEDGER}
                columns={[
                  { header: 'Date', dataKey: 'dateStr' },
                  { header: 'Voucher No', dataKey: 'voucherNumber' },
                  { header: 'Narration', dataKey: 'narration' },
                  { header: 'Amount', dataKey: 'amountStr' },
                ]}
                rows={journalPdfRows}
              />
            </div>

            {/* ================= MOBILE CARDS ================= */}
            <div className="block lg:hidden space-y-2">
              {journalVouchers.map(v => (
                <div key={v._id} className="bg-gray-900 border border-gray-700 rounded-xl p-3 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono text-cyan-300">{v.voucherNumber}</div>
                      <div className="text-gray-500">{new Date(v.date).toLocaleDateString()}</div>
                    </div>
                    <div className="font-bold text-white">₹{v.totalDebit}</div>
                  </div>
                  {v.narration && <div className="text-gray-400 mt-1">{v.narration}</div>}
                </div>
              ))}
              {journalVouchers.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-xs">No journal vouchers yet.</div>
              )}
            </div>

            {/* ================= DESKTOP TABLE ================= */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-xs lg:text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3">Date</th>
                    <th className="text-left py-3">Voucher No</th>
                    <th className="text-left py-3">Narration</th>
                    <th className="text-right py-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {journalVouchers.map(v => (
                    <tr key={v._id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="py-3">{new Date(v.date).toLocaleDateString()}</td>
                      <td className="py-3 font-mono text-cyan-300">{v.voucherNumber}</td>
                      <td className="py-3 text-gray-400">{v.narration || '-'}</td>
                      <td className="py-3 text-right">₹{v.totalDebit}</td>
                    </tr>
                  ))}
                  {journalVouchers.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-8 text-gray-500">No journal vouchers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'pettycash':
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Petty Cash Book</h3>
              {pettyCashLedgers.length > 0 && (
                <PdfButton title="Petty Cash Book" orientation={ORIENTATION.LEDGER} columns={registerPdfColumns} rows={toPdfRows(entriesForLedgerIds(pettyCashLedgers.map(l => l._id)))} />
              )}
            </div>
            {pettyCashLedgers.length === 0 ? (
              <p className="text-gray-500">
                No ledger named "Petty Cash" found. Create one in Ledger Master and post Receipt/Payment
                vouchers against it to see this register. (Counter Memo entries below are a separate quick-log,
                not yet linked to a ledger.)
              </p>
            ) : (
              <RegisterTable rows={entriesForLedgerIds(pettyCashLedgers.map(l => l._id))} />
            )}

            <h4 className="text-lg font-semibold mt-8 mb-3 text-gray-300">Counter Memo Log (Petty Sales/Purchase)</h4>
            <div className="space-y-3">
              {pettyTransactions.slice(0, 10).map((t, i) => (
                <div key={i} className="flex justify-between bg-gray-900 p-4 rounded-2xl">
                  <div>
                    <span className="font-medium">{t.itemName}</span>
                    <p className="text-sm text-gray-500">{t.type} • {t.paymentType}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold">₹{t.amount}</span>
                    <p className="text-xs text-gray-500">{new Date(t.date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {pettyTransactions.length === 0 && (
                <p className="text-gray-500 text-sm">No counter memo entries yet.</p>
              )}
            </div>
          </div>
        );

      case 'pl': {
        const plLedgers = ledgersWithBalance.filter(l => l.group === 'Profit & Loss');
        const income = plLedgers.filter(l => l.balanceType === 'Credit');
        const expenses = plLedgers.filter(l => l.balanceType === 'Debit');
        const totalIncome = income.reduce((s, l) => s + l.balance, 0);
        const totalExpense = expenses.reduce((s, l) => s + l.balance, 0);
        const net = totalIncome - totalExpense;

        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-end mb-2">
              <PdfButton
                title="Profit and Loss Statement"
                orientation={ORIENTATION.LEDGER}
                columns={[{ header: 'Ledger', dataKey: 'name' }, { header: 'Amount', dataKey: 'amountStr' }, { header: 'Section', dataKey: 'section' }]}
                rows={[
                  ...income.map(l => ({ name: l.name, amountStr: `Rs. ${l.balance.toFixed(2)}`, section: 'Income' })),
                  ...expenses.map(l => ({ name: l.name, amountStr: `Rs. ${l.balance.toFixed(2)}`, section: 'Expense' })),
                  { name: `Net ${net >= 0 ? 'Profit' : 'Loss'}`, amountStr: `Rs. ${net.toFixed(2)}`, section: 'Result' },
                ]}
              />
            </div>
            <h3 className="text-2xl font-bold text-cyan-200 mb-6 text-center">Profit &amp; Loss Statement</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-emerald-400 font-semibold mb-3">Income</h4>
                {income.map(l => (
                  <div key={l._id} className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-300">{l.name}</span>
                    <span>₹{l.balance.toFixed(2)}</span>
                  </div>
                ))}
                {income.length === 0 && <p className="text-gray-500 text-sm">No income ledgers yet.</p>}
              </div>
              <div>
                <h4 className="text-rose-400 font-semibold mb-3">Expenses</h4>
                {expenses.map(l => (
                  <div key={l._id} className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-300">{l.name}</span>
                    <span>₹{l.balance.toFixed(2)}</span>
                  </div>
                ))}
                {expenses.length === 0 && <p className="text-gray-500 text-sm">No expense ledgers yet.</p>}
              </div>
            </div>
            <p className={`text-5xl font-bold mt-10 text-center ${net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              ₹{net.toFixed(2)}
            </p>
            <p className="text-gray-500 text-center">Net {net >= 0 ? 'Profit' : 'Loss'}</p>
          </div>
        );
      }

      case 'bs': {
        const bsLedgers = ledgersWithBalance.filter(l => l.group === 'Balance Sheet');
        const assets = bsLedgers.filter(l => l.balanceType === 'Debit');
        const liabilitiesEquity = bsLedgers.filter(l => l.balanceType === 'Credit');
        const totalAssets = assets.reduce((s, l) => s + l.balance, 0);
        const totalLiabEq = liabilitiesEquity.reduce((s, l) => s + l.balance, 0);

        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-end mb-2">
              <PdfButton
                title="Balance Sheet"
                orientation={ORIENTATION.LEDGER}
                columns={[{ header: 'Ledger', dataKey: 'name' }, { header: 'Amount', dataKey: 'amountStr' }, { header: 'Section', dataKey: 'section' }]}
                rows={[
                  ...assets.map(l => ({ name: l.name, amountStr: `Rs. ${l.balance.toFixed(2)}`, section: 'Asset' })),
                  { name: 'Total Assets', amountStr: `Rs. ${totalAssets.toFixed(2)}`, section: '—' },
                  ...liabilitiesEquity.map(l => ({ name: l.name, amountStr: `Rs. ${l.balance.toFixed(2)}`, section: 'Liability/Equity' })),
                  { name: 'Total Liabilities & Equity', amountStr: `Rs. ${totalLiabEq.toFixed(2)}`, section: '—' },
                ]}
              />
            </div>
            <h3 className="text-2xl font-bold text-cyan-200 mb-2 text-center">Balance Sheet</h3>
            <p className="text-gray-400 text-center mb-6">Assets = Liabilities + Equity</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-cyan-400 font-semibold mb-3">Assets</h4>
                {assets.map(l => (
                  <div key={l._id} className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-300">{l.name}</span>
                    <span>₹{l.balance.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 mt-2 font-bold">
                  <span>Total Assets</span><span>₹{totalAssets.toFixed(2)}</span>
                </div>
              </div>
              <div>
                <h4 className="text-amber-400 font-semibold mb-3">Liabilities &amp; Equity</h4>
                {liabilitiesEquity.map(l => (
                  <div key={l._id} className="flex justify-between py-2 border-b border-gray-700">
                    <span className="text-gray-300">{l.name}</span>
                    <span>₹{l.balance.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 mt-2 font-bold">
                  <span>Total Liabilities &amp; Equity</span><span>₹{totalLiabEq.toFixed(2)}</span>
                </div>
              </div>
            </div>
            {Math.abs(totalAssets - totalLiabEq) > 0.01 && (
              <p className="text-amber-400 text-center mt-6 text-sm">
                ⚠️ Out of balance by ₹{Math.abs(totalAssets - totalLiabEq).toFixed(2)} — check opening balances and voucher entries.
              </p>
            )}
          </div>
        );
      }

      case 'stocklist': {
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-end mb-2">
              <PdfButton
                title="Current Stock Position"
                orientation={ORIENTATION.REGISTER}
                columns={[
                  { header: 'Item Code', dataKey: 'itemCode' },
                  { header: 'Type', dataKey: 'type' },
                  { header: 'Item', dataKey: 'itemName' },
                  { header: 'Qty', dataKey: 'qtyStr' },
                  { header: 'Amount', dataKey: 'amountStr' },
                ]}
                rows={stockTransactions.map(tx => ({
                  itemCode: tx.inventoryId?.itemCode || '-',
                  type: tx.type,
                  itemName: tx.inventoryId?.name || 'Unknown',
                  qtyStr: String(tx.qty),
                  amountStr: `Rs. ${tx.amount}`,
                }))}
              />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-cyan-200">Current Stock Position</h3>
            <p className="text-gray-500 text-sm">
              Open the Inventory page for the full editable list. This is a snapshot view derived from stock transactions.
            </p>
          </div>
        );
      }

      case 'stockregister': {
        const stockPdfRows = stockTransactions.map(tx => ({
          dateStr: new Date(tx.date).toLocaleDateString(),
          type: tx.type,
          item: tx.inventoryId?.name || 'Unknown',
          qtyStr: String(tx.qty),
          amountStr: `Rs. ${tx.amount}`,
        }));
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Stock In/Out Register</h3>
              <PdfButton
                title="Stock In-Out Register"
                orientation={ORIENTATION.REGISTER}
                columns={[
                  { header: 'Date', dataKey: 'dateStr' },
                  { header: 'Type', dataKey: 'type' },
                  { header: 'Item', dataKey: 'item' },
                  { header: 'Qty', dataKey: 'qtyStr' },
                  { header: 'Amount', dataKey: 'amountStr' },
                ]}
                rows={stockPdfRows}
              />
            </div>

            {/* ================= MOBILE CARDS ================= */}
            <div className="block lg:hidden space-y-2">
              {stockTransactions.map(tx => (
                <div key={tx._id} className="bg-gray-900 border border-gray-700 rounded-xl p-3 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${
                        tx.type === 'Purchase' || tx.type === 'Sales'
                          ? 'bg-emerald-900 text-emerald-300'
                          : 'bg-rose-900 text-rose-300'
                      }`}>
                        {tx.type}
                      </span>
                      <div className="text-sm font-semibold text-white mt-1">{tx.inventoryId?.name || 'Unknown'}</div>
                      <div className="text-gray-500">{new Date(tx.date).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-400">Qty {tx.qty}</div>
                      <div className="font-bold text-cyan-300">₹{tx.amount}</div>
                    </div>
                  </div>
                </div>
              ))}
              {stockTransactions.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-xs">No stock transactions yet.</div>
              )}
            </div>

            {/* ================= DESKTOP TABLE ================= */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-xs lg:text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3">Date</th>
                    <th className="text-left py-3">Type</th>
                    <th className="text-left py-3">Item</th>
                    <th className="text-center py-3">Qty</th>
                    <th className="text-right py-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {stockTransactions.map(tx => (
                    <tr key={tx._id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="py-3">{new Date(tx.date).toLocaleDateString()}</td>
                      <td className="py-3">{tx.type}</td>
                      <td className="py-3">{tx.inventoryId?.name || 'Unknown'}</td>
                      <td className="py-3 text-center">{tx.qty}</td>
                      <td className="py-3 text-right">₹{tx.amount}</td>
                    </tr>
                  ))}
                  {stockTransactions.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-500">No stock transactions yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'salesregister': {
        const stockSales = stockTransactions.filter(t => t.type === 'Sales');
        const pettySales = pettyTransactions.filter(t => t.type === 'Sales');
        const total = stockSales.reduce((s, t) => s + t.amount, 0) + pettySales.reduce((s, t) => s + t.amount, 0);
        const salesPdfRows = [
          ...stockSales.map(t => ({ item: t.inventoryId?.name || 'Item', qtyStr: String(t.qty), amountStr: `Rs. ${t.amount}`, source: 'Stock' })),
          ...pettySales.map(t => ({ item: t.itemName, qtyStr: '-', amountStr: `Rs. ${t.amount}`, source: 'Counter' })),
        ];
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Sales Register</h3>
              <div className="flex items-center gap-3">
                <span className="text-emerald-400 font-bold">Total: ₹{total.toFixed(2)}</span>
                <PdfButton
                  title="Sales Register"
                  orientation={ORIENTATION.REGISTER}
                  columns={[
                    { header: 'Item', dataKey: 'item' },
                    { header: 'Qty', dataKey: 'qtyStr' },
                    { header: 'Amount', dataKey: 'amountStr' },
                    { header: 'Source', dataKey: 'source' },
                  ]}
                  rows={salesPdfRows}
                />
              </div>
            </div>
            <p className="text-gray-500 text-sm mb-4">Combines Stock-Out (Sales) transactions and Counter Memo sales.</p>
            <div className="space-y-2">
              {stockSales.map(t => (
                <div key={t._id} className="flex justify-between bg-gray-900 p-3 rounded-xl">
                  <span>{t.inventoryId?.name || 'Item'} × {t.qty}</span>
                  <span>₹{t.amount}</span>
                </div>
              ))}
              {pettySales.map((t, i) => (
                <div key={`petty-${i}`} className="flex justify-between items-center bg-gray-900 p-3 rounded-xl">
                  <span>{t.itemName} (Counter)</span>
                  <div className="flex items-center gap-3">
                    <span>₹{t.amount}</span>
                    {t.outstandingAmount > 0 && (
                      <button
                        onClick={() => navigate(`/minierp/tras?open=${t._id}`)}
                        className="w-fit px-2 py-1 bg-rose-700 hover:bg-rose-600 rounded-lg text-xs flex items-center gap-1"
                        title="Open in TraS — Collections"
                      >
                        <FontAwesomeIcon icon={faUserClock} /> TraS
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {stockSales.length === 0 && pettySales.length === 0 && (
                <p className="text-gray-500 text-sm">No sales recorded yet.</p>
              )}
            </div>
          </div>
        );
      }

      case 'purchaseregister': {
        const stockPurchases = stockTransactions.filter(t => t.type === 'Purchase');
        const pettyPurchases = pettyTransactions.filter(t => t.type === 'Purchase');
        const total = stockPurchases.reduce((s, t) => s + t.amount, 0) + pettyPurchases.reduce((s, t) => s + t.amount, 0);
        const purchasePdfRows = [
          ...stockPurchases.map(t => ({ item: t.inventoryId?.name || 'Item', qtyStr: String(t.qty), amountStr: `Rs. ${t.amount}`, source: 'Stock' })),
          ...pettyPurchases.map(t => ({ item: t.itemName, qtyStr: '-', amountStr: `Rs. ${t.amount}`, source: 'Counter' })),
        ];
        return (
          <div className="bg-gray-800 rounded-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-cyan-200">Purchase Register</h3>
              <div className="flex items-center gap-3">
                <span className="text-rose-400 font-bold">Total: ₹{total.toFixed(2)}</span>
                <PdfButton
                  title="Purchase Register"
                  orientation={ORIENTATION.REGISTER}
                  columns={[
                    { header: 'Item', dataKey: 'item' },
                    { header: 'Qty', dataKey: 'qtyStr' },
                    { header: 'Amount', dataKey: 'amountStr' },
                    { header: 'Source', dataKey: 'source' },
                  ]}
                  rows={purchasePdfRows}
                />
              </div>
            </div>
            <p className="text-gray-500 text-sm mb-4">Combines Stock-In (Purchase) transactions and Counter Memo purchases.</p>
            <div className="space-y-2">
              {stockPurchases.map(t => (
                <div key={t._id} className="flex justify-between bg-gray-900 p-3 rounded-xl">
                  <span>{t.inventoryId?.name || 'Item'} × {t.qty}</span>
                  <span>₹{t.amount}</span>
                </div>
              ))}
              {pettyPurchases.map((t, i) => (
                <div key={`petty-${i}`} className="flex justify-between bg-gray-900 p-3 rounded-xl">
                  <span>{t.itemName} (Counter)</span>
                  <span>₹{t.amount}</span>
                </div>
              ))}
              {stockPurchases.length === 0 && pettyPurchases.length === 0 && (
                <p className="text-gray-500 text-sm">No purchases recorded yet.</p>
              )}
            </div>
          </div>
        );
      }

      default:
        return <div className="p-8 text-gray-400">Report under development...</div>;
    }
  };

  // ===== Graph data derived from real records (falls back to empty state, not fake numbers) =====
  const monthlyChartData = useMemo(() => {
    const buildSeries = (records, amountKey = 'amount') => {
      const map = {};
      records.forEach(r => {
        const key = monthKey(r.date);
        map[key] = (map[key] || 0) + (r[amountKey] || 0);
      });
      return Object.keys(map).sort().slice(-6).map(key => ({ label: monthLabel(key), value: Math.round(map[key]) }));
    };

    const sales = [
      ...stockTransactions.filter(t => t.type === 'Sales'),
      ...pettyTransactions.filter(t => t.type === 'Sales'),
    ];
    const purchases = [
      ...stockTransactions.filter(t => t.type === 'Purchase'),
      ...pettyTransactions.filter(t => t.type === 'Purchase'),
    ];
    const receipts = vouchers.filter(v => v.voucherType === 'Receipt');

    // How customers actually pay — Cash vs UPI vs Card vs Credit — the
    // single most intuitive chart for a shop owner, no axes to read.
    const paymentSplit = {};
    pettyTransactions.forEach(t => {
      paymentSplit[t.paymentType] = (paymentSplit[t.paymentType] || 0) + (t.amount || 0);
    });

    return {
      sales: buildSeries(sales),
      purchases: buildSeries(purchases),
      receipts: buildSeries(receipts, 'totalDebit'),
      paymentSplit: Object.entries(paymentSplit).map(([label, value]) => ({ label, value })),
    };
  }, [stockTransactions, pettyTransactions, vouchers]);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-cyan-200 mb-2">Reports &amp; Analytics</h1>
        <p className="text-gray-400 mb-8">Financial • Inventory • Performance Insights</p>

        {/* Main Tabs */}
        <div className="flex border-b border-gray-700 mb-8">
          <button
            onClick={() => { setActiveTab('accounting'); setSelectedReport('ledger'); }}
            className={`w-fit px-2 py-1 font-medium transition-all ${activeTab === 'accounting' ? 'border-b-4 border-cyan-400 text-cyan-200' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={faFileAlt} className="mr-2" /> Accounting
          </button>
          <button
            onClick={() => { setActiveTab('inventory'); setSelectedReport('stocklist'); }}
            className={`w-fit px-2 py-1 font-medium transition-all ${activeTab === 'inventory' ? 'border-b-4 border-cyan-400 text-cyan-200' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={faWarehouse} className="mr-2" /> Inventory
          </button>
          <button
            onClick={() => setActiveTab('graphs')}
            className={`w-fit px-2 py-1 font-medium transition-all ${activeTab === 'graphs' ? 'border-b-4 border-cyan-400 text-cyan-200' : 'text-gray-400'}`}
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-2" /> Graphs
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading reports...</p>
        ) : (
          <>
            {activeTab !== 'graphs' && (
              <>
                <div className="flex flex-wrap gap-2 mb-8">
                  {(activeTab === 'accounting' ? accountingReports : inventoryReports).map(report => (
                    <button
                      key={report.key}
                      onClick={() => setSelectedReport(report.key)}
                      className={`w-fit px-2 py-1 rounded-2xl flex items-center gap-2 text-sm transition-all ${
                        selectedReport === report.key
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={report.icon} />
                      {report.label}
                    </button>
                  ))}
                </div>
                <div>{renderReportContent()}</div>
              </>
            )}

            {activeTab === 'graphs' && (
              <div>
                <p className="text-gray-400 text-sm mb-6">
                  A quick visual picture of your last 6 months — green means money coming in, red means money going out.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <BarChart data={monthlyChartData.sales} title="Monthly Sales" theme="sales" />
                  <BarChart data={monthlyChartData.purchases} title="Monthly Purchases" theme="purchase" />
                  <div className="lg:col-span-2">
                    <LineChart data={monthlyChartData.receipts} title="Receipts Trend" theme="receipts" />
                  </div>
                  <div className="lg:col-span-2">
                    <DonutChart data={monthlyChartData.paymentSplit} title="How Customers Are Paying (Counter Memo)" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MiniERPReports;
